import React from 'react';
import { Dialog, IconButton, Button, Stack, useTheme } from '@mui/material';
import { MdClose, MdPhone, MdCallEnd } from 'react-icons/md';
import { Icon } from '@iconify/react';

/**
 * CallConfirmationDialog - Reusable confirmation dialog before making a call
 *
 * Props:
 *  open         - boolean: whether dialog is visible
 *  onClose      - fn: called when user clicks No or closes
 *  onConfirm    - fn: called when user clicks Yes (should trigger the API call)
 *  userName     - string: name of the user being called
 *  phoneNumber  - string: phone number to display
 *  loading      - boolean: disable Yes button while calling
 */
const CallConfirmationDialog = ({ open, onClose, onConfirm, userName, phoneNumber, loading = false }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 3,
          backgroundColor: isDark ? '#1a1a2e' : '#ffffff'
        },
      }}
    >
      <div className={`card h-100 radius-12 text-center position-relative mb-0 ${isDark ? 'bg-dark-mode border-dark' : ''}`}>
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: isDark ? '#2d3748' : 'background.default',
            color: isDark ? '#e5e7eb' : 'text.primary',
            '&:hover': { bgcolor: isDark ? '#4a5568' : 'background.paper' },
          }}
          size="small"
        >
          <MdClose size={20} />
        </IconButton>

        <div className="card-body p-24">
          {/* Phone Icon */}
          <div className="w-64-px h-64-px d-inline-flex align-items-center justify-content-center bg-success-600 text-white mb-16 radius-12">
            <Icon icon="akar-icons:phone" className="h5 mb-0" />
          </div>

          {/* User Name */}
          {userName && (
            <h6 className="mb-4" style={{ color: isDark ? '#f3f4f6' : '#111827' }}>
              {userName}
            </h6>
          )}

          {/* Phone Number */}
          {phoneNumber && (
            <p 
              className="card-text mb-8 fw-semibold" 
              style={{ 
                fontSize: '22px',
                color: isDark ? '#e5e7eb' : '#374151'
              }}
            >
              {phoneNumber}
            </p>
          )}

          {/* Confirmation Message */}
          <p className="text-md mb-20" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
            Do you want to make a call?
          </p>

          {/* Action Buttons */}
          <Stack direction="row" justifyContent="center" spacing={2}>
            <Button
              variant="outlined"
              color="error"
              onClick={onClose}
              startIcon={<MdCallEnd />}
              disabled={loading}
              sx={{ borderRadius: 2, px: 3 }}
            >
              No
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={onConfirm}
              startIcon={<MdPhone />}
              disabled={loading}
              sx={{ borderRadius: 2, px: 3 }}
            >
              {loading ? 'Calling...' : 'Yes'}
            </Button>
          </Stack>
        </div>
      </div>
    </Dialog>
  );
};

export default CallConfirmationDialog;
